
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import EditButton from "../buttons/EditButton.vue";
import EditFrame from "../EditFrame.vue";
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  History,
} from "tiptap-vuetify";
import { EmailTemplate } from "@/api/dtos/EmailTemplate.schema";
import UpdateableImage from "./UpdateableImage.vue";
import api from "@/api";
import { Operator } from "@/api/dtos/Operator.schema";
import { OperatorsModule } from "@/store/modules/OperatorsModule";
import EditActionButtons from "../EditActionButtons.vue";

@Component({
  components: {
    EditFrame,
    EditButton,
    TiptapVuetify,
    UpdateableImage,
    EditActionButtons,
  },
})
export default class EmailTemplates extends Vue {
  currentlyDraggedChipText: null | string = null;
  selectedTemplate: EmailTemplate | null = null;

  @Prop()
  templates!: EmailTemplate[];

  @Prop({ default: null })
  operatorId!: string | null;

  errorMissingPlaceholders: string | null = null;

  extensions = [
    History,
    Link,
    Underline,
    Strike,
    Italic,
    ListItem,
    BulletList,
    OrderedList,
    [
      Heading,
      {
        options: {
          levels: [1, 2, 3],
        },
      },
    ],
    Bold,
  ];

  content = "";
  editedRtfContent: {
    [templateId: string]: string;
  } = {};

  get selectItems(): {
    text: string;
    value: EmailTemplate;
  }[] {
    return this.templates.map((s) => {
      return { text: this.localizeTemplateName(s.name), value: s };
    });
  }

  localizeTemplateName(str: string): string {
    switch (str) {
      case "reminder":
        return "Erinnerungsmail";
      case "bookingConfirmation":
        return "Buchungsbestätigung";
      case "paymentCancelled":
        return "Bezahlungsabbruch";
      case "confirmedBookingCancelled":
        return "Stornierungs-Mail";
      case "registration":
        return "Registrierungs-Mail";
      case "appLogin":
        return "App-Login";
      case "accountDeleted":
        return "Account-Löschung";
      case "accountLocked":
        return "Account-Sperrung";
      case "emailChanged":
        return "E-Mail-Änderung";
      case "passwordForgotten":
        return "Passwort vergessen";
      case "firmwareUpdateDone":
        return "Firmware-Update durchgeführt";
      case "firmwareUpdateWarning":
        return "Firmware-Update-Warnung";
      case "responsibilityChanged":
        return "Zuständigkeits-Änderung";
      case "privacyPolicyChanged":
        return "Datenschutz-Änderung";
      case "termsAndConditionsChanged":
        return "AGB-Änderung";
    }

    return `<${str}>`;
  }

  operatorLogo: File | null = null;
  editedLogo: File | null = null;

  @Watch("templates")
  onTemplatesUpdated(): void {
    if (this.templates.length === 0) return;
    this.selectedTemplate = this.templates[0];
  }

  @Watch("operatorId")
  onOperatorIdUpdated(): void {
    this.fetchImage();
  }

  @Watch("selectedTemplate")
  onTemplateSelected(template: EmailTemplate): void {
    if (template == null) return;
    const cachedContent = this.editedRtfContent[template.uuid];

    if (cachedContent) this.content = this.editedRtfContent[template.uuid];
    else {
      this.editedRtfContent[template.uuid] = template.richText;
      this.content = template.richText;
    }
  }

  @Watch("content")
  onContentChanged(): void {
    const selectedTemplate = this.selectedTemplate;

    if (selectedTemplate == null) return;

    this.editedRtfContent[selectedTemplate.uuid] = this.content;
  }

  async handleImageUpload(file: File): Promise<void> {
    if (!this.isEditing) return; // Prevent upload when not in edit mode

    if (file.size >= 102400) {
      alert(
        "Es können nur Dateien bis 100 KB gewählt werden. Die aktuell gewählte ist " +
          Math.round(file.size / 1024) +
          " KB groß."
      );
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      const img = new Image();
      if (dataUrl === null)
        throw new Error("File could not be converted to data-Url");
      img.src = dataUrl as string;
      img.onload = async () => {
        // img fertig geladen
        const width = img.width;
        const height = img.height;
        if (width !== height) {
          alert("Das Bild muss quadratisch sein.");
          return;
        }
        this.editedLogo = file;
      };
    };
    reader.readAsDataURL(file);
  }

  async fetchImage() {
    const operatorLogo = await api.OperatorsApi.preloadOperatorLogo(
      this.operatorId!
    );
    if (operatorLogo) {
      this.operatorLogo = operatorLogo;
    }
  }
  $refs!: {
    editFrame: EditFrame;
  };
  isEditing = false;

  async onSave(): Promise<void> {
    if (!this.editedRtfContent[this.selectedTemplate!.uuid]) {
      this.editedRtfContent[this.selectedTemplate!.uuid] = this.content;
    }
    for (const templateId in this.editedRtfContent) {
      const content = this.editedRtfContent[templateId];
      if (!this.operatorId) {
        await api.MailTemplatesApi.updateMailTemplate(templateId, content);
      } else {
        await api.OperatorsApi.updateMailTemplate(
          this.operatorId,
          templateId,
          content
        );
      }
    }
    if (this.editedLogo !== null) {
      await api.OperatorsApi.updateOperatorLogoPng(
        this.operatorId!,
        this.editedLogo
      );
    }
    this.editedLogo = null;
    this.editedRtfContent = {};
    if (this.operatorId) {
      OperatorsModule.refreshDetailedOperator();
    }
  }

  onCancel(): void {
    this.editedRtfContent = {};
    this.content = this.selectedTemplate!.richText;
    this.editedLogo = null;
  }

  @Watch("content")
  validateObligatoryPlaceholders(emailText: string) {
    if (this.selectedTemplate == null) return;
    if (this.selectedTemplate.obligatoryPlaceholders == null) return;
    const missingPlaceholders = [];
    for (const elem of this.selectedTemplate?.obligatoryPlaceholders) {
      if (!emailText.includes(`{${elem}}`)) {
        missingPlaceholders.push(elem);
      }
    }
    if (missingPlaceholders.length > 0) {
      this.errorMissingPlaceholders =
        "Es fehlen die Pflichtfelder " + missingPlaceholders;
    } else {
      this.errorMissingPlaceholders = null;
    }
  }
}
